import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Home, FileSignature, CheckSquare, FileText, MessageSquare, UserRound, Gift, Building, Search, Settings as SettingsIcon, Menu, X, ChevronDown, Users2, Users, ShieldCheck, LifeBuoy, Eye } from "lucide-react";
import NotificationBell from "./NotificationBell";
import WelcomeTour from "./WelcomeTour";
import AgentBrandmark from "./AgentBrandmark";
import SupportBubble from "./SupportBubble";
import VerificationBanner from "./VerificationBanner";
import { TrialPill, TrialProvider, TrialExpiredGate, TrialEndingBanner, useTrial } from "./Trial";
import { useAuth } from "../lib/auth";
import { api, clearImpersonation, getImpersonationMeta, getImpersonationToken } from "../lib/api";

// ── Impersonation / Public-preview banner ─────────────────────────────
// One sticky top strip shared by two closely-related states:
//   • Admin impersonation  → amber, "Currently Viewing as X", Exit button
//   • Public preview (48h) → slate, "PUBLIC PREVIEW", countdown, no exit
// The meta.mode discriminator is set by PreviewFelecia.jsx when it
// stashes the preview JWT; admin impersonation never sets `mode`.
function ImpersonationBanner() {
  const token = getImpersonationToken();
  const meta = getImpersonationMeta();
  if (!token || !meta) return null;

  // Public marketing preview — completely different visual and copy.
  if (meta.mode === "public_preview") {
    const closesAt = meta.window_closes_at || meta.expires_at;
    const closesLabel = closesAt ? new Date(closesAt).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    }) : null;
    return (
      <div
        className="sticky top-0 z-40 w-full text-white shadow"
        style={{ background: "linear-gradient(90deg, #0F172A 0%, #1E293B 100%)" }}
        role="alert"
        aria-live="polite"
        data-testid="public-preview-banner"
      >
        <div className="mx-auto max-w-7xl 2xl:max-w-[1600px] px-4 sm:px-6 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Eye className="h-4 w-4 flex-shrink-0 text-amber-300" />
            <span className="text-xs sm:text-sm font-semibold truncate">
              Public preview · read-only ·
              <span className="font-normal text-white/70 ml-1.5">
                Felecia Monroe's live CRM
              </span>
            </span>
          </div>
          {closesLabel && (
            <span className="hidden sm:inline text-[11px] font-medium text-white/70 shrink-0" data-testid="public-preview-window">
              Closes {closesLabel}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Admin impersonation — the pre-existing behavior.
  const name = meta?.target?.full_name || meta?.target?.email || "Unknown agent";
  const exit = () => {
    clearImpersonation();
    // Bounce back to admin so the admin lands where they came from.
    window.location.href = "/admin";
  };
  return (
    <div
      className="sticky top-0 z-40 w-full bg-amber-500 text-slate-900 border-b-2 border-amber-600 shadow"
      role="alert"
      aria-live="polite"
      data-testid="impersonation-banner"
    >
      <div className="mx-auto max-w-7xl 2xl:max-w-[1600px] px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm font-semibold truncate">
            Currently Viewing as <span className="font-bold" data-testid="impersonation-target-name">{name}</span>
          </span>
          {meta?.actor?.email && (
            <span className="hidden sm:inline text-xs text-amber-900/80 truncate">· admin: {meta.actor.email}</span>
          )}
        </div>
        <button
          type="button"
          onClick={exit}
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-800 shrink-0"
          data-testid="impersonation-exit-btn"
        >
          <X className="h-3.5 w-3.5" /> Exit Impersonation
        </button>
      </div>
    </div>
  );
}

const NAV_ITEMS = [
  { to: "/", end: true, label: "Run My Day", icon: Home, testId: "nav-run-my-day" },
  { to: "/leads", label: "Leads", icon: Users, testId: "nav-leads" },
  { to: "/clients", label: "Clients", icon: UserRound, testId: "nav-clients" },
  { to: "/properties", label: "My Properties", icon: Building, testId: "nav-my-properties" },
  { to: "/transactions", label: "Transactions", icon: FileSignature, testId: "nav-transactions" },
  { to: "/listings", label: "MLS Search", icon: Search, testId: "nav-mls-search" },
  { to: "/messages", label: "Messages", icon: MessageSquare, testId: "nav-messages" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, testId: "nav-tasks" },
  { to: "/referrals", label: "Refer", icon: Gift, testId: "nav-referrals" },
  { to: "/community", label: "Community", icon: Users2, testId: "nav-community" },
];

// Items surfaced only through the user avatar menu. The Document Vault
// lives here (instead of the main nav) because it's a one-off library —
// agents open it a few times per week to add/preview master templates,
// then work happens inside a transaction.
const USER_MENU_ITEMS = [
  { to: "/documents", label: "Document Vault", icon: FileText, testId: "user-menu-vault" },
  { to: "/settings", label: "Profile & settings", icon: SettingsIcon, testId: "user-menu-settings" },
];

const linkBase = "px-2.5 py-2 text-sm font-medium tracking-tight rounded-md whitespace-nowrap transition-colors";
const linkClass = ({ isActive }) =>
  `${linkBase} ${isActive ? "text-white" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"}`;

const drawerLinkClass = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 rounded-md text-base font-medium ${
    isActive ? "text-white" : "text-slate-700 hover:bg-slate-50"
  }`;

// Inline style for active nav links — pulls the agent's primary color.
const activeStyle = { background: "var(--primary-hex)" };

export default function Shell({ children }) {
  return (
    <TrialProvider>
      <ShellInner>{children}</ShellInner>
    </TrialProvider>
  );
}

function ShellInner({ children }) {
  const { agent, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const userMenuRef = useRef(null);

  // Ask the server once whether this agent is on the admin whitelist so we
  // can surface the /admin/feedback link in the profile menu. Fails silent
  // for non-admins (the endpoint always returns is_admin false, not 403).
  useEffect(() => {
    let cancelled = false;
    api.get("/support/whoami")
      .then(({ data }) => { if (!cancelled) setIsAdmin(!!data.is_admin); })
      .catch(() => { /* silent — assume not admin */ });
    return () => { cancelled = true; };
  }, []);

  // Close both menus whenever route changes so tapping a link dismisses them.
  useEffect(() => { setMenuOpen(false); setUserMenu(false); }, [location.pathname]);

  // Outside-click dismissal for the desktop user menu. We can't use a backdrop
  // <div> because the header's `backdrop-blur` creates a containing block that
  // clips fixed descendants in Chromium — clicks below the header would never
  // reach the backdrop. Global mousedown listener sidesteps that entirely.
  useEffect(() => {
    if (!userMenu) return;
    const onDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenu(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [userMenu]);

  // Escape-to-close for keyboard users; harmless when drawer is closed.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  return (
    <div className="min-h-screen">
      <ImpersonationBanner />
      <TrialEndingBanner />
      <VerificationBanner />
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/70 backdrop-blur-md">
        <div className="mx-auto max-w-7xl 2xl:max-w-[1600px] px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 xl:gap-6 min-w-0" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="xl:hidden p-2 -ml-2 rounded-md text-slate-700 hover:bg-slate-100"
              aria-label="Open menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Single identity block: brandmark + name → opens the profile menu.
                Was previously split with an avatar disc on the right — now one clear
                agent-identity anchor. */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setUserMenu(v => !v)}
                className="flex items-center gap-2.5 font-heading font-bold text-slate-900 min-w-0 rounded-md px-1.5 py-1 -mx-1.5 hover:bg-slate-100 transition-colors"
                aria-label="Account menu"
                aria-expanded={userMenu}
                aria-haspopup="menu"
                data-testid="brand-home"
              >
                <AgentBrandmark agent={agent} size={32} />
                <span className="tracking-tight truncate max-w-[180px] hidden sm:inline" data-testid="brand-label">
                  {agent?.brokerage_name || agent?.full_name || ""}
                </span>
                <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 hidden sm:inline" aria-hidden="true" />
              </button>
              {userMenu && (
                <div className="absolute left-0 top-full mt-2 w-64 rounded-lg bg-white shadow-xl border border-slate-200 py-2 z-40" role="menu" data-testid="user-menu">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <div className="text-sm font-semibold text-slate-900 truncate" data-testid="agent-name">{agent?.full_name}</div>
                    <div className="text-xs text-slate-500 truncate">{agent?.email}</div>
                    {agent?.brokerage_name && (
                      <div className="mt-1 text-xs text-slate-500 truncate">{agent.brokerage_name}</div>
                    )}
                  </div>
                  <Link to="/documents" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="user-menu-vault">
                    <FileText className="h-4 w-4 text-slate-500" /> Document Vault
                  </Link>
                  <Link to="/settings" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="user-menu-settings">
                    <SettingsIcon className="h-4 w-4 text-slate-500" /> Profile & settings
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setUserMenu(false);
                      // Fire the global event SupportBubble is listening for.
                      window.dispatchEvent(new CustomEvent("ikey:open-support"));
                    }}
                    className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    data-testid="user-menu-support"
                  >
                    <LifeBuoy className="h-4 w-4 text-slate-500" /> App Feedback & Support
                  </button>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="user-menu-admin">
                      <ShieldCheck className="h-4 w-4 text-slate-500" /> Admin dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => { setUserMenu(false); logout(); nav("/login"); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4 text-slate-500" /> Log out
                  </button>
                </div>
              )}
            </div>

            <nav className="hidden xl:flex items-center gap-0.5 flex-1 min-w-0 overflow-hidden">
              {NAV_ITEMS.map(({ to, end, label, icon: Icon, testId }) => (
                <NavLink
                  key={to} to={to} end={end}
                  className={linkClass}
                  style={({ isActive }) => isActive ? activeStyle : undefined}
                  data-testid={testId}
                  title={label}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="hidden 2xl:inline">{label}</span>
                  </span>
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <TrialPill />
            <NotificationBell />
          </div>
        </div>
      </header>

      {/* Mobile / tablet drawer */}
      {menuOpen && (
        <div className="xl:hidden fixed inset-0 z-40" data-testid="mobile-menu-overlay">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute left-0 top-0 h-full w-[280px] max-w-[85vw] bg-white shadow-xl flex flex-col" role="dialog" aria-label="Navigation">
            <div className="flex items-center justify-between px-4 h-14 border-b border-slate-200">
              <div className="flex items-center gap-2.5 font-heading font-bold text-slate-900 min-w-0">
                <AgentBrandmark agent={agent} size={32} />
                <span className="tracking-tight truncate" data-testid="brand-label-mobile">
                  {agent?.brokerage_name || agent?.full_name || ""}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="p-2 -mr-2 rounded-md text-slate-500 hover:bg-slate-100"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-4 py-4 border-b border-slate-200 2xl:hidden">
              <div className="text-sm font-medium text-slate-900 truncate">{agent?.full_name}</div>
              <div className="text-xs text-slate-500 truncate">{agent?.email}</div>
            </div>
            <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
              {NAV_ITEMS.map(({ to, end, label, icon: Icon, testId }) => (
                <NavLink
                  key={to} to={to} end={end}
                  className={drawerLinkClass}
                  style={({ isActive }) => isActive ? activeStyle : undefined}
                  data-testid={`${testId}-mobile`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{label}</span>
                </NavLink>
              ))}
              <div className="my-2 border-t border-slate-100" />
              {USER_MENU_ITEMS.map(({ to, label, icon: Icon, testId }) => (
                <NavLink
                  key={to} to={to}
                  className={drawerLinkClass}
                  style={({ isActive }) => isActive ? activeStyle : undefined}
                  data-testid={`${testId}-mobile`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{label}</span>
                </NavLink>
              ))}
              <button
                onClick={() => { setMenuOpen(false); logout(); nav("/login"); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-base font-medium text-slate-700 hover:bg-slate-50"
                data-testid="logout-btn-mobile"
              >
                <LogOut className="h-5 w-5 text-slate-500" />
                <span>Log out</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8">
        <ShellGate>{children}</ShellGate>
      </main>
      <WelcomeTour />
      <SupportBubble />
    </div>
  );
}

/**
 * ShellGate — either renders the actual page content or the trial-expired
 * paywall. Kept inside the shell so the top nav (with the "subscribe" CTA in
 * the user menu) stays reachable. `/settings` is exempted so the agent can
 * actually get to the checkout button.
 */
function ShellGate({ children }) {
  const { hasAccess, loading } = useTrial();
  const { pathname } = useLocation();
  // Settings must be reachable so the user can actually subscribe.
  if (loading || hasAccess || pathname.startsWith("/settings")) return children;
  return <TrialExpiredGate />;
}
